import { Crown, Gem, Users, Zap, HeadphonesIcon, Lock } from "lucide-react";
import { useState, useEffect } from "react";

const benefits = [
  {
    icon: Crown,
    title: "Invite-Only Access",
    description: "You don't just join—you're chosen for a league reserved for the elite.",
    gradient: "from-primary to-secondary",
  },
  {
    icon: Gem,
    title: "0% Commission. Forever.",
    description: "Every diamond you earn is yours. No hidden fees, no surprises, no exceptions.",
    gradient: "from-secondary to-tertiary",
  },
  {
    icon: Zap,
    title: "Premium Creator Network",
    description: "Our relationship with TikTok means you get insider access, priority support, and platform advantages.",
    gradient: "from-tertiary to-accent",
  },
  {
    icon: Users,
    title: "Inner Circle of Influencers",
    description: "Connect, create, and grow with the creators everyone else wants to meet.",
    gradient: "from-accent to-primary",
  },
  {
    icon: HeadphonesIcon,
    title: "24/7 VIP Support",
    description: "Dedicated team on call whenever you need. Problems solved in hours, not days.",
    gradient: "from-primary to-accent",
  },
  {
    icon: Lock,
    title: "Stay on Your Terms",
    description: "No fine print. No forced commitments. You stay because VCM delivers.",
    gradient: "from-secondary to-accent",
  },
];

const Benefits = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('benefits');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="benefits" className="py-24 md:py-32 bg-card relative overflow-hidden">
      {/* Enhanced premium background with more gradients */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1200px] bg-gradient-to-br from-primary/8 via-secondary/5 to-accent/8 rounded-full blur-[200px]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-accent/10 via-tertiary/5 to-transparent rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-secondary/8 via-primary/5 to-transparent rounded-full blur-[150px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">
            The VCM Advantage
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="gradient-text">Privileges of the Inner Circle</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            For creators with standards as high as their ambitions.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div
                key={index}
                className={`group relative p-8 rounded-2xl bg-gradient-to-br from-background via-background to-card border border-border hover:border-primary/50 transition-all duration-500 hover:shadow-[0_0_60px_rgba(217,70,239,0.2)] ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${100 * index}ms` }}
              >
                {/* Icon with gradient background */}
                <div className={`mb-6 inline-flex p-4 rounded-xl bg-gradient-to-br ${benefit.gradient} shadow-lg group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(217,70,239,0.3)] transition-all duration-300`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold mb-3 text-foreground group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>

                {/* Enhanced hover glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 via-secondary/3 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-primary/20 via-transparent to-accent/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ zIndex: -1 }} />
              </div>
            );
          })}
        </div>

        {/* Bottom emphasis with enhanced styling */}
        <div 
          className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex flex-wrap items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border border-primary/30 shadow-[0_0_30px_rgba(217,70,239,0.15)]">
            <span className="text-muted-foreground">Trusted by</span>
            <span className="font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">100+ elite creators</span>
            <span className="text-muted-foreground">earning</span>
            <span className="font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">$10M+ annually</span>
          </div>
        </div>

        {/* Bottom gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
      </div>
    </section>
  );
};

export default Benefits;
